import { Component, Inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { GlobalTenantContractsApi, TenantContractModel, TenantContractsApi } from 'src/app/usage-api';
import { ConfirmComponent, ConfirmData } from 'src/app/shared/modals/confirm/confirm.component';
import { MatDialog } from '@angular/material/dialog';
import { SB_PANEL_MESSAGE, SnackbarService } from 'src/app/shared/snackbar.service';
import { ApiError } from 'src/app/shared/model/utility';
import { catchError, delay, filter, map, of, switchMap, tap, throwError, finalize } from 'rxjs';
import { ContractsDataSource } from '../../data-table/datasource/contracts-datasource';
import { FileUploadHelper } from '../../model/fileupload-helper';

@Component({
  selector: 'app-contract-list-view',
  templateUrl: './contract-list-view.component.html',
  styleUrls: ['./contract-list-view.component.scss'],
  standalone: false
})
export class ContractListViewComponent implements OnInit {
  dataSource: ContractsDataSource;
  title = 'BackOffice.ManageContracts.TenantContracts';
  pageSize: number = 25;
  additionalColumns: string[] = ['delete'];

  apiWaitMs: number = 1000;
  loading = signal<boolean>(false);

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly tenantContractsApi: TenantContractsApi,
    private readonly globalTenantContractsApi: GlobalTenantContractsApi,
    private readonly sb: SnackbarService,
  ) {}

  ngOnInit() {
    this.dataSource = new ContractsDataSource(this.globalTenantContractsApi, null, null, null, false);
  }

  onRowClicked(contract: TenantContractModel) {
    this.router.navigate(['/back-office/contracts', contract.tenantId, contract.id]);
  }

  newContractClick() {
    this.router.navigate(['back-office', 'contracts', 'create']);
  }

  confirmDeleteContract(contract: TenantContractModel) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '600px',
      data: {
        title: `Delete Contract: ${contract.tenantName}`,
        message: 'Are you sure you want to delete this contract?',
        requireInput: true,
        inputPhrase: 'DELETE',
        inputPlaceholder: 'Enter <strong>DELETE</strong> to confirm',
      } as ConfirmData,
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.deleteContract(contract);
      }
    });
  }

  exportContracts() {
    this.loading.set(true);
    this.globalTenantContractsApi
      .globalTenantContractsExportContracts({})
      .pipe(
        switchMap((result) => FileUploadHelper.download(result, `${new Date().toISOString()}-contracts.csv`)),
        finalize(() => this.loading.set(false)),
        catchError((error) => {
          this.displayErrorMessage('Error exporting contracts');
          return throwError(() => error);
        }),
      )
      .subscribe();
  }

  uploadContracts() {
    this.loading.set(true);
    FileUploadHelper.upload<Blob>('.csv', (content) => new Blob([content], { type: 'text/csv' }))
      .pipe(
        filter((file) => file.type === 'complete'),
        map((file) => file.content),
        switchMap((file) => {
          return this.globalTenantContractsApi.globalTenantContractsImportContracts(file).pipe(
            delay(this.apiWaitMs),
            catchError((error) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              this.displayErrorMessage(error?.error?.errors?.join(', ') ?? 'Error uploading contracts');
              return throwError(() => error);
            }),
            tap((result) => {
              if (result?.isSuccess) {
                this.dataSource.loadData(true);
                this.displayStatusMessage(result.message ?? 'Contracts uploaded');
              } else {
                this.displayErrorMessage(result?.errors?.join(', ') ?? 'Error uploading contracts');
              }
            }),
          );
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe();
  }

  deleteContract(contract: TenantContractModel) {
    this.tenantContractsApi
      .tenantContractsRemoveContract(contract?.tenantId, contract.id)
      .pipe(
        delay(this.apiWaitMs),
        map((_) => {
          this.dataSource.loadData(true);
          this.displayStatusMessage('Contract removed');
        }),
        catchError((_: ApiError) => {
          this.displayErrorMessage('Error deleting contract');
          return of(null);
        }),
      )
      .subscribe();
  }

  onDeleteClick(event: Event, contract: TenantContractModel) {
    event.stopPropagation();
    this.confirmDeleteContract(contract);
  }

  displayStatusMessage(message: string) {
    this.sb.open(message, null, { duration: 3000, panelClass: SB_PANEL_MESSAGE });
  }

  displayErrorMessage(message: string) {
    this.sb.open(message, null, { duration: 3000, panelClass: SB_PANEL_MESSAGE });
  }
}
